import { Component, Inject, Injector, OnInit, OnDestroy, ViewChild, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmLeaveDialogComponent, ConfirmLeaveResult } from 'src/app/modals/confirm-leave-dialog/confirm-leave-dialog.component';
import { EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, filter, map, tap, takeUntil } from 'rxjs/operators';
import { getEnumList } from 'src/app/_helpers/enum-helper';
import { InitialQualitativeEnum, QuantitativeDecisionStatisticEnum, QuantitativeDecisionStatisticNamesEnum } from 'src/app/enums/quantitativeDecisionStatistic.enum';
import { StepperContainerComponent } from 'src/app/shared/stepper/stepper-container.component';
import { Lookup } from 'src/app/models/lookup.model';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { RiskAssessment, SampleFilterDimension } from 'src/app/models/risk-assessment.model';
import { RiskAssessmentScopeAgent, RiskAssessmentScopeOptionsResponse } from 'src/app/models/risk-assessment-scope.model';
import { AgentJurisdiction } from 'src/app/models/agent-jurisdiction.model';
import { AssessmentStatusEnum } from 'src/app/enums/assessmentStatus.enum';
import { SampleSurveyTypeService } from 'src/app/services/sample-survey-type.service';
import { SampleFilterOption } from 'src/app/common-components/sample-filter-dimension/sample-filter-dimension.component';
import {
  SHIFT_COVERAGE_OPTIONS,
  SAMPLE_TYPE_OPTIONS,
  emptyDimension,
  surveyRowsToDimension,
  dimensionToSurveyRows,
  buildSampleFilterSummaryRows,
} from 'src/app/shared/utils/sample-filter.util';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { RiskAssessmentService } from 'src/app/services/risk-assessment.service';
import { JurisdictionService } from 'src/app/services/jurisdiction.service';
import { JurisdictionLookup } from 'src/app/models/jurisdictionLookup.model';
import { OelTypeService } from 'src/app/services/oel-type.service';
import { AgentJurisdictionService } from 'src/app/services/agent-jurisdiction.service';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { CheckboxFormField, DateFormField, HierarchicalSelectionFormField, MultiSelectionFormField, RadioFormField, SingleSelectionFormField, TextFormField, ToggleFormField, FormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { StepConfig, StepperConfig } from 'src/app/shared/stepper/stepper.model';
import { AgentsListComponent } from 'src/app/components/agents-list/agents-list.component';
import { AgentGroupListComponent } from 'src/app/components/agent-group-list/agent-group-list.component';
import { SegListComponent } from 'src/app/components/seg-list/seg-list.component';
import { RoutePaths } from 'src/app/shared/constants/routePaths.constants';

export interface IRiskAssessmentDialogData extends IFormDialogData {
  cloneFrom?: RiskAssessment;
}

@Component({
  standalone: false,
  selector: 'app-risk-assessment-dialog',
  templateUrl: './risk-assessment-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./risk-assessment-dialog.component.scss']
})
export class RiskAssessmentDialogComponent extends FormComponent<RiskAssessment> implements OnInit, OnDestroy {
  @ViewChild('raStepTpl', { static: true }) raStepTpl!: TemplateRef<unknown>;
  @ViewChild(StepperContainerComponent) stepper?: StepperContainerComponent;

  // DASH-3331: the property name of the Decision Statistic control, so the template can render the
  // EN 689 server error against that specific field (rather than a magic string).
  readonly decisionStatisticFieldProperty = nameOf<RiskAssessment>((x) => x.quantitativeDecisionStatistic);
  // DASH-3331: server-side EN 689 guard message for the Decision Statistic control. The jurisdiction sits
  // AFTER the statistic in the wizard, so this contradiction can only be caught on submit, not at selection.
  decisionStatisticServerError: string | null = null;

  organizationsList: OrganizationLevel[];
  quantitativeDecisionStatisticList = [];
  riskAssessmentList: Lookup[];
  jurisdictionList: Lookup[] = [];
  oelTypeList: Lookup[];
  allJurisdictions: Lookup[] = [];
  cloneSource: RiskAssessment;

  stepperConfig: StepperConfig;
  currentStep: StepConfig;
  basicsFields: FormField[] = [];
  exposureLimitsFields: FormField[] = [];
  fallbackFields: FormField[] = [];
  cloneFields: FormField[] = [];
  agentsFields: FormField[] = [];
  segsFields: FormField[] = [];
  fallbackExpanded = false;
  readonly startEmptyValue = InitialQualitativeEnum['Start Empty'];
  readonly copyFromValue = InitialQualitativeEnum['Copy From'];

  oelGapLoading = false;
  oelGapAgentNames: string[] = [];
  showAllGapAgents = false;
  readonly maxGapAgentsShown = 5;

  // ─── G19 sample filters (DASH-2976) ──────────────────────────────────────
  readonly shiftCoverageOptions: SampleFilterOption[] = SHIFT_COVERAGE_OPTIONS;
  readonly sampleTypeOptions: SampleFilterOption[] = SAMPLE_TYPE_OPTIONS;
  surveyTypeOptions: SampleFilterOption[] = [];
  // The three dimensions the shared control drives. Shift coverage and sample type round-trip on
  // entity.sampleFilters; survey type is stored as a GUID-keyed row set on entity.surveyTypeFilters.
  shiftCoverageFilter: SampleFilterDimension<number> = emptyDimension<number>();
  sampleTypeFilter: SampleFilterDimension<number> = emptyDimension<number>();
  surveyTypeFilter: SampleFilterDimension<string> = emptyDimension<string>();

  // Snapshot of the hydrated filter state taken in initSampleFilters(). On edit we only re-serialise the
  // filters when the user actually changed them; if they are untouched we omit both keys so the API leaves
  // the persisted value alone (BE: "null => leave untouched"). Sending an All/All object for an RA whose
  // SampleFilters column is still NULL (every pre-DASH-2970 row) would read as an edit and force a full
  // recalculation of every register — see onBeforeSave().
  private baselineFilters = '';

  // Pre-computed once from the current filter state (refreshed on init + each dimension change) rather than
  // via a template getter, so it does not re-run on every change-detection tick. Shared with the review step.
  sampleFilterSummaryRows: { label: string; value: string }[] = [];
  // Save-validity per dimension, driven by the control's (validChange). Default true (All is valid).
  shiftCoverageValid = true;
  sampleTypeValid = true;
  surveyTypeValid = true;

  // ─── Agents & SEGs scope steps (add-only) ────────────────────────────────
  scopeOptions: RiskAssessmentScopeOptionsResponse = { agents: [], segs: [] };
  private scopeOptionsLoaded = false;
  useAgentGroups = false;
  hasAgentGroups = false;
  agentLookups: Lookup[] = [];
  groupLookups: Lookup[] = [];
  segLookups: Lookup[] = [];
  agentBannerText = '';
  agentBannerIsError = false;
  segBannerText = '';
  segBannerIsError = false;
  segBannerAdvisory = '';

  private orgChange$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<RiskAssessmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IRiskAssessmentDialogData,
    protected injector: Injector,
    protected riskAssessmentService: RiskAssessmentService,
    protected dateFormatService: DateFormatService,
    private organizationLevelService: OrganizationLevelService,
    private jurisdictionService: JurisdictionService,
    private oelTypeService: OelTypeService,
    private agentJurisdictionService: AgentJurisdictionService,
    private sampleSurveyTypeService: SampleSurveyTypeService,
    private agentGroupService: AgentGroupService,
    private dialog: MatDialog,
    private router: Router
  ) {
    super(
      dialogRef,
      riskAssessmentService,
      data,
      "Risk Assessment",
      injector
    );
    this.cloneSource = data?.cloneFrom;
  }

  ngOnInit(): void {
    this.isFormLoading = true;
    this.dialogRef.backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.confirmAndClose());
    this.orgChange$.pipe(
      filter(orgId => !!orgId),
      switchMap(orgId => forkJoin({
        jurisdictions: this.jurisdictionService.getAll(JSON.stringify({ organizationLevelId: orgId, addedToMySite: true })),
        siteJurisdictionId: this.getSiteJurisdictionId(orgId)
      }).pipe(
        catchError(error => {
          this.toastService.showError(error);
          return EMPTY;
        })
      )),
      takeUntil(this.destroy$)
    ).subscribe(({ jurisdictions, siteJurisdictionId }) => {
      this.applySiteJurisdictions(jurisdictions, siteJurisdictionId);
      this.changeDetectorRef.detectChanges();
    });
    const decisionStatistics = getEnumList(QuantitativeDecisionStatisticNamesEnum).map(res => ({
      id: QuantitativeDecisionStatisticNamesEnum[res],
      name: res
    }));
    this.quantitativeDecisionStatisticList = [
      ...decisionStatistics.filter(s => s.id === QuantitativeDecisionStatisticEnum.ArithmeticMean95UCL),
      ...decisionStatistics.filter(s => s.id !== QuantitativeDecisionStatisticEnum.ArithmeticMean95UCL)
    ];
    forkJoin({
      entity: this.getEntity(),
      organizations: this.organizationLevelService.getAll(),
      riskAssessments: this.riskAssessmentService.getAll(),
      jurisdictions: this.jurisdictionService.getAll(),
      oelTypes: this.oelTypeService.getAll(),
      surveyTypes: this.sampleSurveyTypeService.getAll()
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.entity = result.entity;
        this.organizationsList = result.organizations;
        this.riskAssessmentList = result.riskAssessments;
        this.allJurisdictions = result.jurisdictions;
        this.oelTypeList = result.oelTypes;
        this.surveyTypeOptions = (result.surveyTypes ?? []).map(s => ({ id: s.id, name: s.name }));
        this.initModel();
        this.initSampleFilters();
        this.initFields();
        this.buildStepperConfig();
        if (this.isAdd) {
          this.orgChange$.next(this.entity.organizationLevelId);
        }
        this.isFormLoading = false;
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.isFormLoading = false;
        this.toastService.displayErrorMessage('Failed to load form data. Please try again.', true);
        this.dialogRef.close();
      }
    });
  }

  get isCloneMode(): boolean {
    return !!this.cloneSource;
  }

  // Clone is an add, so the base getEntity() would return a blank object and the dialog's
  // grid row is all we would have to prefill from. That row is missing surveyTypeFilters (mapped
  // .ExplicitExpansion() on the grid projection — DASH-2982) and would silently widen the survey-type
  // dimension. Fetch the source assessment by id through the existing forkJoin so the full filter state
  // arrives before the dialog renders, and prefill from it (never from the row). The entity itself stays
  // a fresh create object. A failed load surfaces via the forkJoin error handler (dialog closes with a
  // toast) rather than silently falling back to unfiltered defaults.
  override getEntity(): Observable<RiskAssessment> {
    if (this.isCloneMode) {
      return this.riskAssessmentService.getById(this.cloneSource.id).pipe(
        tap(source => { this.cloneSource = source; }),
        map(() => this.getNewObject())
      );
    }
    return super.getEntity();
  }

  initModel() {
    if (this.isCloneMode) {
      this.prefillFromCloneSource();
    }

    if (this.entity.periodStart) {
      this.entity.periodStart =
        this.dateFormatService.convertTimestampToDate(this.entity.periodStart as number);
    }
    if (this.entity.periodEnd) {
      this.entity.periodEnd =
        this.dateFormatService.convertTimestampToDate(this.entity.periodEnd as number);
    }
    this.entity.initialQualitativeSelector = this.entity.copyFromRiskAssessmentId ?
      InitialQualitativeEnum['Copy From'] : InitialQualitativeEnum['Start Empty'];

    if (!this.entity.oelTypeId) {
      const twa8Id = this.oelTypeList.find(o => o.name === 'TWA8')?.id;
      this.entity.oelTypeId = typeof twa8Id === 'string' ? twa8Id : '';
    }

    // Restore the progressive-disclosure expanded state when editing an RA
    // that already has fallback lab-result inclusion configured.
    this.fallbackExpanded = !!this.entity.enableOelTypeFallback || !!this.entity.enableJurisdictionFallback;

    if (this.isAdd) {
      this.onCloneSourceChanged();
    }
  }

  private prefillFromCloneSource() {
    const source = this.cloneSource;
    this.entity.name = `Copy of ${source.name || 'Risk Assessment'}`;
    this.entity.periodStart = source.periodStart;
    this.entity.periodEnd = source.periodEnd;
    this.entity.quantitativeDecisionStatistic = source.quantitativeDecisionStatistic;
    this.entity.organizationLevelId = source.organizationLevelId;
    this.entity.jurisdictionId = source.jurisdictionId;
    this.entity.oelTypeId = source.oelTypeId;
    this.entity.copyFromRiskAssessmentId = source.id;
    // Prefill the three sample-filter dimensions as editable defaults. Deep-clone so edits on the clone
    // never mutate the fetched source. An unfiltered source (no sampleFilters / no surveyTypeFilters)
    // stays unfiltered — initSampleFilters() falls back to the All dimension for each absent field.
    this.entity.sampleFilters = source.sampleFilters ? structuredClone(source.sampleFilters) : undefined;
    this.entity.surveyTypeFilters = source.surveyTypeFilters
      ? structuredClone(source.surveyTypeFilters)
      : undefined;
  }

  initFields() {
    const tt = (key: string) => this.translateService.getDynamicTranslation(key);

    this.basicsFields = [
      new TextFormField({
        property: nameOf<RiskAssessment>((x) => x.name),
        name: "Name",
        columnSpan: new ColumnSpan(1, 1),
        required: true
      }),
      new DateFormField({
        property: nameOf<RiskAssessment>((x) => x.periodStart),
        name: "Start Date",
        columnSpan: new ColumnSpan(1, 2),
        required: true
      }),
      new DateFormField({
        property: nameOf<RiskAssessment>((x) => x.periodEnd),
        name: "End Date",
        columnSpan: new ColumnSpan(1, 2),
        required: true
      }),
      new HierarchicalSelectionFormField({
        property: nameOf<RiskAssessment>((x) => x.organizationLevelId),
        name: "Organization Level",
        options: toOrganizationTree(this.organizationsList),
        columnSpan: new ColumnSpan(1, 1),
        required: true,
        onChange: () => this.onOrganizationLevelChanged()
      }),
      new SingleSelectionFormField({
        property: nameOf<RiskAssessment>((x) => x.quantitativeDecisionStatistic),
        name: "Decision Statistic",
        options: this.quantitativeDecisionStatisticList,
        columnSpan: new ColumnSpan(1, 1),
        required: true,
        onChange: () => this.decisionStatisticServerError = null
      }),
    ];

    this.exposureLimitsFields = [
      new SingleSelectionFormField({
        property: nameOf<RiskAssessment>((x) => x.jurisdictionId),
        name: "Primary Jurisdiction",
        tooltip: tt("The regulatory jurisdiction whose OEL values will be used to score all samples in this assessment. Auto-set from the site associated with the selected org level."),
        // Edit locks this field, so it needs the full list to display a jurisdiction since removed from the site.
        options: this.isAdd ? this.jurisdictionList : this.allJurisdictions,
        columnSpan: new ColumnSpan(1, 2),
        required: true,
        onChange: () => this.decisionStatisticServerError = null
      }),
      new SingleSelectionFormField({
        property: nameOf<RiskAssessment>((x) => x.oelTypeId),
        name: "Primary OEL Type",
        tooltip: tt("The OEL type (e.g. TWA8, TWA90) that statistics are calculated against. An agent must have an OEL defined for this exact type in this jurisdiction — otherwise its register will show samples but no statistics."),
        options: this.oelTypeList,
        columnSpan: new ColumnSpan(1, 2),
        required: true
      }),
      new ToggleFormField({
        property: nameOf<RiskAssessment>((x) => x.applyShiftCorrection),
        name: "Apply Shift Correction",
        tooltip: tt("Adjusts the OEL for shifts longer or shorter than the standard 8-hour workday using the Brief & Scala model or jurisdiction-specific method."),
        columnSpan: new ColumnSpan(1, 1),
        required: false
      }),
    ];

    // Fallback controls live inside the "Include lab results from other sources"
    // progressive-disclosure section (rendered separately in the template).
    this.fallbackFields = [
      new ToggleFormField({
        property: nameOf<RiskAssessment>((x) => x.fallbackToAnyOelType),
        name: "Also include lab results recorded under any OEL type",
        tooltip: tt("When on, lab results filed under any OEL type are pulled into registers, not just those filed under the primary OEL type. Statistics still use the primary OEL type only."),
        columnSpan: new ColumnSpan(1, 1),
        required: false,
        onChange: () => this.applyOelTypeFallbackChanges()
      }),
      new SingleSelectionFormField({
        property: nameOf<RiskAssessment>((x) => x.fallbackOelTypeId),
        name: "Specific OEL type to include",
        tooltip: tt("Pull in lab results recorded under this additional OEL type. Useful when labs file results under TWA8 but your primary is TWA90."),
        options: this.oelTypeList,
        columnSpan: new ColumnSpan(1, 1),
        required: true,
        isVisible: () => !this.entity.fallbackToAnyOelType
      }),
      new ToggleFormField({
        property: nameOf<RiskAssessment>((x) => x.enableJurisdictionFallback),
        name: "Also include lab results from a different jurisdiction",
        tooltip: tt("When on, lab results filed under a different jurisdiction are pulled into registers. Statistics still use the primary jurisdiction only."),
        columnSpan: new ColumnSpan(1, 1),
        required: false,
        onChange: () => this.applyJurisdictionChanges()
      }),
      new SingleSelectionFormField({
        property: nameOf<RiskAssessment>((x) => x.fallbackJurisdictionId),
        name: "Specific jurisdiction to include",
        tooltip: tt("The secondary jurisdiction whose lab results will be included. You must specify one."),
        options: this.allJurisdictions,
        columnSpan: new ColumnSpan(1, 1),
        required: true,
        isVisible: () => this.entity.enableJurisdictionFallback
      }),
    ];

    this.cloneFields = [
      new SingleSelectionFormField({
        property: nameOf<RiskAssessment>((x) => x.copyFromRiskAssessmentId),
        name: "Copy from",
        options: this.riskAssessmentList,
        columnSpan: new ColumnSpan(1, 1),
        required: true,
        onChange: () => this.onCloneSourceChanged()
      }),
      new CheckboxFormField({
        property: nameOf<RiskAssessment>((x) => x.copyQualitativeScores),
        name: "Qualitative scores (consequence & likelihood ratings)",
        columnSpan: new ColumnSpan(1, 1),
        required: false,
        isVisible: () => !!this.entity.copyFromRiskAssessmentId
      }),
      new CheckboxFormField({
        property: nameOf<RiskAssessment>((x) => x.copyControls),
        name: "Controls",
        columnSpan: new ColumnSpan(1, 1),
        required: false,
        isVisible: () => !!this.entity.copyFromRiskAssessmentId
      }),
      new CheckboxFormField({
        property: nameOf<RiskAssessment>((x) => x.copyNotes),
        name: "Notes and comments",
        columnSpan: new ColumnSpan(1, 1),
        required: false,
        isVisible: () => !!this.entity.copyFromRiskAssessmentId
      }),
      new CheckboxFormField({
        property: nameOf<RiskAssessment>((x) => x.copyPerSegDateOverrides),
        name: "Per-SEG date overrides",
        columnSpan: new ColumnSpan(1, 1),
        required: false,
        isVisible: () => !!this.entity.copyFromRiskAssessmentId
      }),
      new CheckboxFormField({
        property: nameOf<RiskAssessment>((x) => x.copyApprovalStatuses),
        name: "Approval statuses",
        columnSpan: new ColumnSpan(1, 1),
        required: false,
        isVisible: () => !!this.entity.copyFromRiskAssessmentId
      }),
      new CheckboxFormField({
        property: nameOf<RiskAssessment>((x) => x.copyAssessmentMethod),
        name: "Assessment method per register (Qualitative / Quantitative / Hazard N/A)",
        columnSpan: new ColumnSpan(1, 1),
        required: false,
        isVisible: () => !!this.entity.copyFromRiskAssessmentId
      }),
    ];

    this.buildScopeFields(tt);

    // Single flat list still drives FormComponent.updateFieldVisibility() / isDisabled().
    this.fields = [...this.basicsFields, ...this.exposureLimitsFields, ...this.fallbackFields, ...this.cloneFields, ...this.agentsFields, ...this.segsFields];
    this.applyJurisdictionChanges();
    this.applyOelTypeFallbackChanges();
    this.applyEditModeFieldLock();
  }

  private buildScopeFields(tt: (key: string) => string) {
    const nameSearchField = [nameOf<Lookup>(x => x.name)];

    this.agentsFields = [
      new RadioFormField({
        property: nameOf<RiskAssessment>(x => x.selectedBy),
        cssClass: 'agents-radio',
        name: "",
        required: false,
        options: [
          { id: 0, name: tt("Individual Agents") },
          { id: 1, name: tt("Agent Groups") }
        ],
        columnSpan: new ColumnSpan(1, 1),
        isVisible: () => this.hasAgentGroups,
        onChange: (value: number) => this.onScopeModeChange(value)
      }),
      new MultiSelectionFormField({
        isVisible: () => this.useAgentGroups === false,
        property: nameOf<RiskAssessment>(x => x.scopeAgentIds), name: "Agents", required: true,
        options: this.agentLookups, columnSpan: new ColumnSpan(1, 3),
        searchKeys: nameSearchField, onChange: () => this.onIndividualAgentsChanged(),
        optionTemplateFields: nameSearchField,
        optionTemplateLabelMap: { name: 'Name' },
        searchComponent: AgentsListComponent,
        additonalSearchData: {}
      }),
      new MultiSelectionFormField({
        isVisible: () => this.useAgentGroups === true,
        property: nameOf<RiskAssessment>(x => x.scopeAgentGroupIds), name: "Agent Groups", required: true,
        options: this.groupLookups, columnSpan: new ColumnSpan(1, 2),
        searchKeys: nameSearchField, onChange: () => this.onAgentGroupsChanged(),
        optionTemplateFields: nameSearchField,
        optionTemplateLabelMap: { name: 'Name' },
        searchComponent: AgentGroupListComponent,
        additonalSearchData: {}
      }),
      new MultiSelectionFormField({
        property: nameOf<RiskAssessment>(x => x.resolvedGroupAgents), name: "Group's Agents", required: false,
        options: [], columnSpan: new ColumnSpan(1, 2), cssClass: 'chip-items', readonly: true,
        isVisible: () => this.useAgentGroups === true,
        selectionTemplate: (opt: FormField) => {
          if (!opt.options.length) return `<span class="chip-empty">${tt("Select one or more agent groups to see their agents.")}</span>`;
          return `<ul class="chip-list">${opt.options.map((res: Lookup) => `<li class="chip">${res.name}</li>`).join('')}</ul>`;
        }
      }),
      new MultiSelectionFormField({
        isVisible: () => this.useAgentGroups === true,
        property: nameOf<RiskAssessment>(x => x.additionalAgentIds), name: "Add additional agents", required: false,
        options: this.agentLookups, columnSpan: new ColumnSpan(1, 2),
        searchKeys: nameSearchField, onChange: () => this.onAdditionalAgentsChanged(),
        optionTemplateFields: nameSearchField,
        optionTemplateLabelMap: { name: 'Name' },
        searchComponent: AgentsListComponent,
        additonalSearchData: {}
      }),
    ];

    this.segsFields = [
      new MultiSelectionFormField({
        property: nameOf<RiskAssessment>(x => x.scopeSegIds), name: "SEGs", required: true,
        options: this.segLookups, columnSpan: new ColumnSpan(1, 1),
        searchKeys: nameSearchField, onChange: () => this.onSegsChanged(),
        optionTemplateFields: nameSearchField,
        optionTemplateLabelMap: { name: 'Name' },
        searchComponent: SegListComponent,
        additonalSearchData: {}
      }),
    ];
  }

  /**
   * A Risk Assessment cannot be recalculated once created, so on edit only the
   * Name remains editable. Every other field is made read-only (greyed out and
   * non-interactive) to make clear those values can no longer be changed.
   */
  private applyEditModeFieldLock() {
    if (this.isAdd) return;
    const nameProperty = nameOf<RiskAssessment>((x) => x.name);
    this.fields
      .filter(field => field.property !== nameProperty)
      .forEach(field => (field.readonly = true));
  }

  private buildStepperConfig() {
    const cancelButton = {
      label: 'Cancel',
      class: 'transparent-btn bordered-btn px-5',
      action: () => this.confirmAndClose(),
      disabled: () => this.isLoading
    };
    this.stepperConfig = {
      orientation: 'horizontal',
      linear: true,
      steps: [
        {
          id: 'basics',
          label: 'Basics',
          template: this.raStepTpl,
          fields: this.basicsFields,
          canProceed: () => this.isBasicsValid(),
          buttons: [cancelButton]
        },
        {
          id: 'exposureLimits',
          label: 'Exposure Limits',
          template: this.raStepTpl,
          fields: this.exposureLimitsFields,
          canProceed: () => this.isExposureLimitsValid(),
          buttons: [cancelButton]
        },
        {
          id: 'startingPoint',
          label: 'Starting Point',
          template: this.raStepTpl,
          fields: this.cloneFields,
          visible: () => this.isAdd,
          canProceed: () => this.isStartingPointValid(),
          buttons: [cancelButton]
        },
        {
          id: 'agents',
          label: 'Agents',
          template: this.raStepTpl,
          fields: this.agentsFields,
          visible: () => this.isAdd,
          canProceed: () => this.isAgentsStepValid(),
          buttons: [cancelButton]
        },
        {
          id: 'segs',
          label: 'SEGs',
          template: this.raStepTpl,
          fields: this.segsFields,
          visible: () => this.isAdd,
          canProceed: () => this.isSegsStepValid(),
          buttons: [cancelButton]
        },
        {
          id: 'sampleFilters',
          label: 'Sample Filters',
          template: this.raStepTpl,
          fields: [],
          // No `visible` predicate: the filter step must survive both add and edit — filters are the one
          // control that stays live after create (AC1/AC12).
          canProceed: () => this.areSampleFiltersValid(),
          buttons: [cancelButton]
        },
        {
          id: 'review',
          label: 'Review',
          template: this.raStepTpl,
          fields: [],
          canProceed: () => true,
          buttons: [
            cancelButton,
            {
              label: this.isAdd ? 'Create Assessment' : 'Save Changes',
              class: 'custom-main-btn px-5',
              action: () => this.submitForm(),
              disabled: () => this.isLoading,
              loading: () => this.isLoading,
              loadingLabel: this.isAdd ? 'Risk Assessment is being created…' : 'Saving changes…'
            }
          ]
        }
      ]
    };
    this.currentStep = this.stepperConfig.steps[0];
  }

  onStepChanged(step: StepConfig) {
    this.currentStep = step;
    if (step.id === 'review') {
      this.fetchAgentOelGaps();
    }
    if (step.id === 'agents' || step.id === 'segs') {
      this.refreshScopeSearchData();
      this.loadScopeStepData();
    }
  }

  // ─── Agents & SEGs scope steps (add-only) ────────────────────────────────

  /** Keeps each grid picker's Added To My Site filter in sync with the currently selected org level. */
  private refreshScopeSearchData() {
    const searchData = { addedToMySite: true, organizationLevelId: this.entity.organizationLevelId };
    [...this.agentsFields, ...this.segsFields]
      .filter(field => !!field.searchComponent)
      .forEach(field => (field.additonalSearchData = searchData));
  }

  private setFieldOptions(property: string, options: Lookup[]) {
    const field = [...this.agentsFields, ...this.segsFields].find(f => f.property === property);
    if (field) field.options = options;
  }

  /** Fetched once, the first time the Agents step is entered — the org level is fixed by then. */
  private loadScopeStepData() {
    if (this.scopeOptionsLoaded || !this.entity.organizationLevelId) return;
    this.scopeOptionsLoaded = true;
    forkJoin({
      scopeOptions: this.riskAssessmentService.getScopeOptions(this.entity.organizationLevelId),
      agentGroups: this.agentGroupService.getAll(JSON.stringify({ organizationLevelId: this.entity.organizationLevelId })),
      scopeUsed: this.entity.copyFromRiskAssessmentId
        ? this.riskAssessmentService.getScopeUsed(this.entity.copyFromRiskAssessmentId)
        : of(null)
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ scopeOptions, agentGroups, scopeUsed }) => {
        this.scopeOptions = scopeOptions;
        this.agentLookups = scopeOptions.agents.map(a => ({ id: a.id, name: a.name }));
        this.segLookups = scopeOptions.segs.map(s => ({ id: s.id, name: s.name }));
        this.groupLookups = agentGroups ?? [];
        this.hasAgentGroups = this.groupLookups.length > 0;
        this.setFieldOptions(nameOf<RiskAssessment>(x => x.scopeAgentIds), this.agentLookups);
        this.setFieldOptions(nameOf<RiskAssessment>(x => x.additionalAgentIds), this.agentLookups);
        this.setFieldOptions(nameOf<RiskAssessment>(x => x.scopeAgentGroupIds), this.groupLookups);
        this.setFieldOptions(nameOf<RiskAssessment>(x => x.scopeSegIds), this.segLookups);
        this.applyScopeDefaults(scopeUsed?.agentIds, scopeUsed?.segIds);
        this.refreshAgentBanner();
        this.refreshSegBanner();
        this.updateFieldVisibility();
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.scopeOptionsLoaded = false;
        this.refreshAgentBanner();
        this.refreshSegBanner();
        this.toastService.displayErrorMessage('Failed to load agents and SEGs for this org level. Please try again.', true);
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /**
   * Fresh create selects every candidate (AC2). A clone lands in Individual Agents mode regardless of
   * how the source assessment was scoped, since a source stores resolved registers, not the group
   * choice that produced them (AC9) — reconstructing a group selection would be a guess.
   */
  private applyScopeDefaults(cloneAgentIds?: string[], cloneSegIds?: string[]) {
    this.useAgentGroups = false;
    this.entity.selectedBy = 0;
    this.entity.scopeAgentIds = cloneAgentIds ? [...cloneAgentIds] : this.scopeOptions.agents.map(a => a.id);
    this.entity.scopeSegIds = cloneSegIds ? [...cloneSegIds] : this.scopeOptions.segs.map(s => s.id);
    this.entity.scopeAgentGroupIds = [];
    this.entity.additionalAgentIds = [];
    this.entity.resolvedGroupAgents = [];
  }

  onScopeModeChange(value: number) {
    this.useAgentGroups = value === 1;
    this.entity.selectedBy = value;
    this.updateFieldVisibility();
    this.refreshAgentBanner();
  }

  onAgentGroupsChanged() {
    const groupIds = this.entity.scopeAgentGroupIds ?? [];
    const resolved = this.scopeOptions.agents.filter((a: RiskAssessmentScopeAgent) =>
      (a.agentGroupIds ?? []).some(g => groupIds.includes(g)));
    this.entity.resolvedGroupAgents = resolved.map(a => a.id);
    this.setFieldOptions(nameOf<RiskAssessment>(x => x.resolvedGroupAgents), resolved.map(a => ({ id: a.id, name: a.name })));
    this.refreshAgentBanner();
  }

  onAdditionalAgentsChanged() {
    this.refreshAgentBanner();
  }

  onIndividualAgentsChanged() {
    this.refreshAgentBanner();
  }

  onSegsChanged() {
    this.refreshSegBanner();
  }

  /** useAgentGroups===false → the individual selection; true → groups' agents union additional agents (deduped). */
  private resolvedAgentIds(): string[] {
    if (!this.useAgentGroups) return this.entity.scopeAgentIds ?? [];
    const fromGroups = this.entity.resolvedGroupAgents ?? [];
    const extra = this.entity.additionalAgentIds ?? [];
    return Array.from(new Set([...fromGroups, ...extra]));
  }

  isAgentsStepValid(): boolean {
    return this.resolvedAgentIds().length > 0;
  }

  isSegsStepValid(): boolean {
    return (this.entity.scopeSegIds?.length ?? 0) > 0;
  }

  private refreshAgentBanner() {
    const tt = (key: string, params?: Record<string, unknown>) => this.translateService.getDynamicTranslation(key, params);
    const resolved = this.resolvedAgentIds();
    const n = resolved.length;
    if (!n) {
      this.agentBannerIsError = true;
      this.agentBannerText = tt('Select at least one agent.');
      return;
    }
    this.agentBannerIsError = false;
    if (!this.useAgentGroups) {
      this.agentBannerText = tt('{{n}} agents selected', { n });
      return;
    }
    const fromGroups = (this.entity.resolvedGroupAgents ?? []).length;
    const groupCount = (this.entity.scopeAgentGroupIds ?? []).length;
    const extra = n - fromGroups;
    this.agentBannerText = extra > 0
      ? tt('{{n}} agents — {{fromGroups}} from {{groupCount}} groups, plus {{extra}} added individually', { n, fromGroups, groupCount, extra })
      : tt('{{n}} agents — {{fromGroups}} from {{groupCount}} groups', { n, fromGroups, groupCount });
  }

  private refreshSegBanner() {
    const tt = (key: string, params?: Record<string, unknown>) => this.translateService.getDynamicTranslation(key, params);
    const a = this.resolvedAgentIds().length;
    const s = this.entity.scopeSegIds?.length ?? 0;
    if (!s) {
      this.segBannerIsError = true;
      this.segBannerText = tt('Select at least one SEG.');
      this.segBannerAdvisory = '';
      return;
    }
    this.segBannerIsError = false;
    const n = a * s;
    this.segBannerText = tt('This will create {{a}} agents × {{s}} SEGs = {{n}} Risk Registers.', { a, s, n });
    this.segBannerAdvisory = n > 500
      ? tt('Creating {{n}} Risk Registers may take a few minutes. Consider narrowing your selection.', { n })
      : '';
  }

  get scopeSummaryRows(): { label: string; value: string }[] {
    if (!this.isAdd) return [];
    const tt = (key: string, params?: Record<string, unknown>) => this.translateService.getDynamicTranslation(key, params);
    const a = this.resolvedAgentIds().length;
    const s = this.entity.scopeSegIds?.length ?? 0;
    const agentsValue = this.useAgentGroups
      ? tt('{{n}} agents — {{groupCount}} groups + {{extra}} added individually', {
        n: a,
        groupCount: (this.entity.scopeAgentGroupIds ?? []).length,
        extra: a - (this.entity.resolvedGroupAgents ?? []).length
      })
      : tt('{{n}} agents', { n: a });
    return [
      { label: tt('Agents'), value: agentsValue },
      { label: tt('SEGs'), value: tt('{{s}} SEGs → {{n}} Risk Registers will be created', { s, n: a * s }) }
    ];
  }

  isBasicsValid(): boolean {
    return !!this.entity?.name
      && !!this.entity?.periodStart
      && !!this.entity?.periodEnd
      && new Date(this.entity.periodEnd) >= new Date(this.entity.periodStart)
      && this.entity?.quantitativeDecisionStatistic != null
      && !!this.entity?.organizationLevelId;
  }

  isExposureLimitsValid(): boolean {
    if (!this.entity?.jurisdictionId || !this.entity?.oelTypeId) return false;
    if (!this.fallbackExpanded) return true;
    const oelTypeOk = this.entity.fallbackToAnyOelType || !!this.entity.fallbackOelTypeId;
    const jurisdictionOk = !this.entity.enableJurisdictionFallback || !!this.entity.fallbackJurisdictionId;
    return oelTypeOk && jurisdictionOk;
  }

  isStartingPointValid(): boolean {
    if (this.entity?.initialQualitativeSelector === InitialQualitativeEnum['Start Empty']) return true;
    return !!this.entity?.copyFromRiskAssessmentId;
  }

  // ─── G19 sample filters (DASH-2976) ──────────────────────────────────────

  /** True when the assessment is Approved — the filter step renders as a static, read-only summary. */
  get isApproved(): boolean {
    return this.entity?.assessmentStatus === AssessmentStatusEnum.Approved;
  }

  /** True when a fresh tenant has no configured survey types — the survey dimension can only match (None). */
  get hasNoSurveyTypes(): boolean {
    return !this.surveyTypeOptions?.length;
  }

  /** Hydrate the three dimension view-models from the loaded entity (round-trips on edit — AC1). */
  private initSampleFilters(): void {
    this.shiftCoverageFilter = this.entity?.sampleFilters?.shiftCoverage ?? emptyDimension<number>();
    this.sampleTypeFilter = this.entity?.sampleFilters?.sampleType ?? emptyDimension<number>();
    // BE-6 (DASH-2982) restored the read map, so entity.surveyTypeFilters now hydrates from real rows on
    // edit; surveyRowsToDimension() handles a null/absent map (older data / no filter) by falling back to All.
    this.surveyTypeFilter = surveyRowsToDimension(this.entity?.surveyTypeFilters);
    // Snapshot the hydrated state so onBeforeSave() can tell whether the user actually touched the filters.
    this.baselineFilters = this.serializeFilters();
    this.refreshSampleFilterSummaryRows();
  }

  /** Stable serialisation of the three dimensions, used to detect whether the user changed the filters. */
  private serializeFilters(): string {
    return JSON.stringify([this.shiftCoverageFilter, this.sampleTypeFilter, this.surveyTypeFilter]);
  }

  onShiftCoverageChange(value: SampleFilterDimension<number | string>): void {
    this.shiftCoverageFilter = value as SampleFilterDimension<number>;
    this.refreshSampleFilterSummaryRows();
  }

  onSampleTypeChange(value: SampleFilterDimension<number | string>): void {
    this.sampleTypeFilter = value as SampleFilterDimension<number>;
    this.refreshSampleFilterSummaryRows();
  }

  onSurveyTypeChange(value: SampleFilterDimension<number | string>): void {
    this.surveyTypeFilter = value as SampleFilterDimension<string>;
    this.refreshSampleFilterSummaryRows();
  }

  /**
   * Gate the step on every dimension being in a BE-saveable shape. Approved assessments show a static
   * summary (no editable controls), so validity is not a concern there.
   */
  areSampleFiltersValid(): boolean {
    if (this.isApproved) return true;
    return this.shiftCoverageValid && this.sampleTypeValid && this.surveyTypeValid;
  }

  /**
   * Recompute the shared summary rows (AC9) from the current filter state. Called on init and after each
   * dimension change instead of from a template getter, avoiding re-computation on every CD tick.
   */
  private refreshSampleFilterSummaryRows(): void {
    const tt = (key: string) => this.translateService.getDynamicTranslation(key);
    this.sampleFilterSummaryRows = buildSampleFilterSummaryRows(
      { shiftCoverage: this.shiftCoverageFilter, sampleType: this.sampleTypeFilter },
      dimensionToSurveyRows(this.surveyTypeFilter),
      this.surveyTypeOptions,
      tt
    );
  }

  toggleFallback() {
    if (!this.isAdd) return;
    this.fallbackExpanded = !this.fallbackExpanded;
    if (!this.fallbackExpanded) {
      this.entity.enableOelTypeFallback = false;
      this.entity.fallbackToAnyOelType = false;
      this.entity.fallbackOelTypeId = null;
      this.entity.enableJurisdictionFallback = false;
      this.entity.fallbackJurisdictionId = null;
    }
    this.updateFieldVisibility();
  }

  applyJurisdictionChanges() {
    const field = this.fallbackFields?.find(f => f.property === nameOf<RiskAssessment>(x => x.fallbackJurisdictionId));
    if (field) field.required = this.entity.enableJurisdictionFallback;
    if (!this.entity.enableJurisdictionFallback) this.entity.fallbackJurisdictionId = null;
    this.updateFieldVisibility();
  }

  applyOelTypeFallbackChanges() {
    const field = this.fallbackFields?.find(f => f.property === nameOf<RiskAssessment>(x => x.fallbackOelTypeId));
    if (field) field.required = this.fallbackExpanded && !this.entity.fallbackToAnyOelType;
    if (this.entity.fallbackToAnyOelType) this.entity.fallbackOelTypeId = null;
    this.updateFieldVisibility();
  }

  selectInitialQualitative(value: number) {
    this.entity.initialQualitativeSelector = value;
    if (value === InitialQualitativeEnum['Start Empty']) {
      this.entity.copyFromRiskAssessmentId = null;
    }
    this.onCloneSourceChanged();
  }

  private onCloneSourceChanged() {
    const hasClone = !!this.entity.copyFromRiskAssessmentId;
    this.entity.copyQualitativeScores = hasClone;
    this.entity.copyControls = hasClone;
    this.entity.copyNotes = hasClone;
    this.entity.copyPerSegDateOverrides = hasClone;
    this.entity.copyApprovalStatuses = hasClone;
    this.entity.copyAssessmentMethod = hasClone;
    this.updateFieldVisibility();
  }

  get gapAgentsToShow(): string[] {
    return this.showAllGapAgents ? this.oelGapAgentNames : this.oelGapAgentNames.slice(0, this.maxGapAgentsShown);
  }

  get hiddenGapAgentCount(): number {
    return Math.max(0, this.oelGapAgentNames.length - this.maxGapAgentsShown);
  }

  get selectedJurisdictionName(): string {
    return this.allJurisdictions?.find(j => j.id === this.entity?.jurisdictionId)?.name ?? '';
  }

  get selectedOelTypeName(): string {
    return this.oelTypeList?.find(o => o.id === this.entity?.oelTypeId)?.name ?? '';
  }

  get selectedOrgLevelName(): string {
    return this.organizationsList?.find(o => o.id === this.entity?.organizationLevelId)?.name ?? '';
  }

  get selectedDecisionStatisticName(): string {
    return this.quantitativeDecisionStatisticList?.find(s => s.id === this.entity?.quantitativeDecisionStatistic)?.name ?? '';
  }

  get cloneSourceName(): string {
    return this.riskAssessmentList?.find(r => r.id === this.entity?.copyFromRiskAssessmentId)?.name ?? '';
  }

  get reviewRows(): { label: string; value: string }[] {
    const tt = (key: string) => this.translateService.getDynamicTranslation(key);
    const dash = '—';
    const fmt = (date: Date | number | string) =>
      this.dateFormatService.formatDateTimeToString(date as Date, this.dateFormatService.shortDateFormat);
    const period = (this.entity?.periodStart && this.entity?.periodEnd)
      ? `${fmt(this.entity.periodStart)} – ${fmt(this.entity.periodEnd)}`
      : dash;
    return [
      { label: tt('Name'), value: this.entity?.name || dash },
      { label: tt('Period'), value: period },
      { label: tt('Organization Level'), value: this.selectedOrgLevelName || dash },
      { label: tt('Decision Statistic'), value: tt(this.selectedDecisionStatisticName) || dash },
      { label: tt('Primary Jurisdiction'), value: this.selectedJurisdictionName || dash },
      { label: tt('Primary OEL Type'), value: this.selectedOelTypeName || dash },
      { label: tt('Shift Correction'), value: tt(this.entity?.applyShiftCorrection ? 'On' : 'Off') },
      { label: tt('Lab result inclusion'), value: this.labResultInclusionSummary },
      ...this.scopeSummaryRows,
      ...this.sampleFilterSummaryRows,
      { label: tt('Clone source'), value: this.cloneSourceName || tt('None') },
    ];
  }

  get labResultInclusionSummary(): string {
    const tt = (key: string) => this.translateService.getDynamicTranslation(key);
    if (!this.fallbackExpanded) return tt('Primary OEL type and jurisdiction only');

    const oelPart = this.entity.fallbackToAnyOelType
      ? tt('Any OEL type')
      : (this.entity.fallbackOelTypeId
        ? `${tt('OEL types')}: ${this.selectedOelTypeName}, ${this.fallbackOelTypeName}`
        : tt('Primary OEL type only'));

    const jurisdictionPart = this.entity.enableJurisdictionFallback && this.entity.fallbackJurisdictionId
      ? `${tt('jurisdictions')}: ${this.selectedJurisdictionName}, ${this.fallbackJurisdictionName}`
      : tt('primary jurisdiction only');

    return `${oelPart}, ${jurisdictionPart}`;
  }

  private get fallbackOelTypeName(): string {
    return this.oelTypeList?.find(o => o.id === this.entity?.fallbackOelTypeId)?.name ?? '';
  }

  private get fallbackJurisdictionName(): string {
    return this.allJurisdictions?.find(j => j.id === this.entity?.fallbackJurisdictionId)?.name ?? '';
  }

  /**
   * Best-effort detection of the 313/316 scenario: agents that have no OEL
   * defined for the primary jurisdiction + primary OEL type combination.
   * Such agents will show samples but no statistics. Warn-not-block.
   */
  fetchAgentOelGaps() {
    const jurisdictionId = this.entity?.jurisdictionId;
    const oelTypeId = this.entity?.oelTypeId;
    if (!jurisdictionId || !oelTypeId) {
      this.oelGapAgentNames = [];
      return;
    }
    this.oelGapLoading = true;
    this.showAllGapAgents = false;
    // DASH-3139: scope the gap check to the RA's selected org level so only agents actually added to
    // that site (via AgentOrgLevel) are considered — matching which agents the RA will include.
    this.agentJurisdictionService.getJurisdictionDetails(jurisdictionId, this.entity?.organizationLevelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((records: AgentJurisdiction[]) => {
        this.oelGapAgentNames = this.computeGapAgents(records ?? [], oelTypeId);
        this.oelGapLoading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  private computeGapAgents(records: AgentJurisdiction[], oelTypeId: string): string[] {
    const byAgent = new Map<string, { name: string; hasOel: boolean }>();
    records.forEach(r => {
      const existing = byAgent.get(r.masterAgentId) ?? { name: r.masterAgentName, hasOel: false };
      if (r.oelTypeId === oelTypeId && r.oel != null) existing.hasOel = true;
      byAgent.set(r.masterAgentId, existing);
    });
    return Array.from(byAgent.values())
      .filter(a => !a.hasOel)
      .map(a => a.name)
      .filter(Boolean);
  }

  submitForm() {
    this.decisionStatisticServerError = null;
    this.save();
  }

  // On create, register generation now runs asynchronously in the worker service, so the API returns
  // the assessment before any registers exist. Navigate straight to the detail page, which renders the
  // generation-in-progress state and polls for completion, instead of just reloading the grid.
  override afterSaveAction(response: RiskAssessment): void {
    this.isLoading = false;
    this.toastService.showSuccess('Success', true);
    if (this.isAdd && response?.id) {
      this.dialogRef.close(true);
      this.router.navigate([RoutePaths.riskAssessment, response.id]);
      return;
    }
    this.closeDialog(response);
  }

  // The backend refuses an arithmetic-mean UCL statistic under an EN 689 jurisdiction
  // (GuardDecisionStatisticAgainstEn689 → 400). The jurisdiction sits after the statistic in the wizard,
  // so this contradiction can only surface on submit. The guard now returns a field-keyed
  // ValidationProblemDetails, so a 400 carrying an error under the Decision Statistic field identifies
  // itself by key — no message text matching, which would break on non-English tenants. Render that
  // message as a field-level error and jump the user back to it. Any other error (a 400 keyed to a
  // different field, a plain-text body, a non-400) falls through to the default toast. On error save()
  // does not close, so the user's other input is preserved.
  handleError = (error: unknown): void => {
    this.isLoading = false;

    if (error instanceof HttpErrorResponse && error.status === 400) {
      const fieldErrors = error.error?.errors?.[this.decisionStatisticFieldProperty];
      if (fieldErrors?.length) {
        this.decisionStatisticServerError = Array.isArray(fieldErrors) ? fieldErrors.join(' ') : fieldErrors;
        this.stepper?.goToStep(0);
        return;
      }
    }

    this.toastService.showError(error);
  };

  confirmAndClose() {
    this.dialog.open(ConfirmLeaveDialogComponent, { width: '400px' })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ConfirmLeaveResult) => {
        if (result === ConfirmLeaveResult.Discard) {
          this.close();
        }
      });
  }

  validate() {
    // Per-step canProceed gates enforce field-level validation before reaching Review.
    const scopeValid = !this.isAdd || (this.isAgentsStepValid() && this.isSegsStepValid());
    return this.isBasicsValid() && this.isExposureLimitsValid() && this.isStartingPointValid() && scopeValid && this.areSampleFiltersValid();
  }

  getNewObject(): RiskAssessment {
    return new RiskAssessment();
  }

  onBeforeSave(model: RiskAssessment) {
    // createdDate / approvedDate are not editable in this form. The API returns them as
    // numeric (epoch) timestamps and JSON.parse(JSON.stringify(entity)) keeps them numeric,
    // so on edit they are already in the stored format and must be passed through untouched.
    // Calling convertDateToTimestamp() on a number throws "number.getTime is not a function",
    // which previously aborted save() synchronously before the Update API call — so editing
    // any field appeared to do nothing and no request was made (DASH-2372). Only convert when
    // the value is still a Date/date-string (defensive, e.g. if ever set via a picker).
    if (model.approvedDate && typeof model.approvedDate !== 'number') {
      model.approvedDate = this.dateFormatService.convertDateToTimestamp(new Date(model.approvedDate));
    }
    if (model.createdDate && typeof model.createdDate !== 'number') {
      model.createdDate = this.dateFormatService.convertDateToTimestamp(new Date(model.createdDate));
    }
    const startDate = new Date(new Date((model.periodStart)).setHours(0, 0, 0, 0));
    const endDate = new Date(new Date((model.periodEnd)).setHours(23, 59, 0, 0));
    model.periodEnd = this.dateFormatService.convertDateToTimestamp(endDate);
    model.periodStart = this.dateFormatService.convertDateToTimestamp(startDate);
    // Normalise the progressive-disclosure UI state into the engine fields.
    // Engine behaviour is unchanged — these only widen lab-result inclusion.
    if (!this.fallbackExpanded) {
      model.enableOelTypeFallback = false;
      model.fallbackToAnyOelType = false;
      model.fallbackOelTypeId = null;
      model.enableJurisdictionFallback = false;
      model.fallbackJurisdictionId = null;
    } else {
      model.enableOelTypeFallback = model.fallbackToAnyOelType || !!model.fallbackOelTypeId;
      if (model.fallbackToAnyOelType) model.fallbackOelTypeId = null;
      model.fallbackJurisdictionId = model.enableJurisdictionFallback
        ? (model.fallbackJurisdictionId ?? null)
        : null;
    }

    // G19 sample filters (DASH-2976). An Approved assessment is read-only here, so we do not rebuild the
    // filters from the controls. The deep-cloned model still carries the persisted filters unchanged, but
    // because the BE compares filter content (not presence) that identical round-trip is not treated as a
    // filter edit — so an otherwise-valid Name edit is not blocked by a 400 (Approved RAs reject filter
    // changes).
    //
    // For everyone else, only serialise the filters when the user actually changed them (or on create).
    // The BE treats a null/absent value as "leave the persisted filters untouched", but a NULL SampleFilters
    // column (every RA created before DASH-2970 — no backfill) differs from an All/All object, so blindly
    // sending All/All on an unrelated edit (e.g. a Name typo fix) would register as a filter change and force
    // a synchronous recalculation of every register. Likewise dimensionToSurveyRows(All) => [] is a
    // destructive clear of the survey-type rows on the BE. So: if untouched, omit both keys entirely.
    //
    // When we DO send them, always send BOTH sampleFilters dimensions in full (a partial write silently
    // clears the omitted dimension per BE-3) and the survey-type row set derived from its dimension.
    if (!this.isApproved) {
      const changed = this.serializeFilters() !== this.baselineFilters;
      if (this.isAdd || changed) {
        model.sampleFilters = {
          shiftCoverage: this.shiftCoverageFilter,
          sampleType: this.sampleTypeFilter,
        };
        model.surveyTypeFilters = dimensionToSurveyRows(this.surveyTypeFilter);
      } else {
        // Untouched: leave the persisted filters alone (null => no-op on the BE).
        delete model.sampleFilters;
        delete model.surveyTypeFilters;
      }
    }

    // Agents/SEGs scope (add-only, never revisited on edit — see model comment). Group selection is an
    // FE-only input device for assembling the agent set, resolved here into flat id lists; the API never
    // sees selectedBy/scopeAgentGroupIds/additionalAgentIds/resolvedGroupAgents.
    if (this.isAdd) {
      model.scopeAgentIds = this.resolvedAgentIds();
    } else {
      delete model.scopeAgentIds;
      delete model.scopeSegIds;
    }
    delete model.selectedBy;
    delete model.scopeAgentGroupIds;
    delete model.additionalAgentIds;
    delete model.resolvedGroupAgents;
  }

  onOrganizationLevelChanged() {
    this.entity.jurisdictionId = null;
    this.jurisdictionList = [];
    this.setPrimaryJurisdictionOptions();
    this.orgChange$.next(this.entity.organizationLevelId);
  }

  private getSiteJurisdictionId(orgId: string): Observable<string | undefined> {
    const orgJurisdictionId = this.organizationsList?.find(o => o.id === orgId)?.jurisdictionId;
    if (orgJurisdictionId) {
      return of(orgJurisdictionId);
    }
    return this.organizationLevelService.getParentSite(orgId).pipe(map(site => site?.jurisdictionId));
  }

  // Only jurisdictions added to the selected site are valid choices, so a prefilled (clone) or site-default
  // jurisdiction that is not on the site is dropped rather than silently kept.
  private applySiteJurisdictions(jurisdictions: JurisdictionLookup[], siteJurisdictionId: string | undefined) {
    this.jurisdictionList = jurisdictions ?? [];
    this.setPrimaryJurisdictionOptions();
    const isOnSite = (id: string) => !!id && this.jurisdictionList.some(j => j.id === id);
    if (isOnSite(this.entity.jurisdictionId)) return;
    this.entity.jurisdictionId = isOnSite(siteJurisdictionId) ? siteJurisdictionId : null;
  }

  private setPrimaryJurisdictionOptions() {
    const field = this.exposureLimitsFields.find(f => f.property === nameOf<RiskAssessment>(x => x.jurisdictionId));
    if (field) field.options = this.jurisdictionList;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
